import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { upsertUserProfile } from '../services/profilesService';
import './Login.css';

const Login = () => {
  const [isSignup, setIsSignup] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { signin, signup, signinWithGoogle, resetPassword } = useAuth();

  useEffect(() => {
    try {
      const msg = sessionStorage.getItem('authMessage');
      if (msg) {
        setError(msg);
        sessionStorage.removeItem('authMessage');
      }
    } catch {
      // ignore storage access failures
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignup) {
        const cred = await signup(email, password);
        const user = cred?.user;
        // Create profile for new email signups as Vendor with status Active
        if (user?.uid) {
          try {
            await upsertUserProfile(user.uid, {
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              email: email.trim(),
              userType: 'Vendor',
              userStatus: 'Active',
              linkApproval: 'n/a'
            });
          } catch (profileErr) {
            console.warn('Failed to create profile on signup:', profileErr?.message || profileErr);
          }
        }
      } else {
        await signin(email, password);
      }
    } catch (error) {
      setError(error.message);
    }
    
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const cred = await signinWithGoogle();
      const user = cred?.user;
      if (user?.uid && user?.email) {
        try {
          // For Google sign-in, only update email and name if profile doesn't have them
          // This prevents overwriting existing profile data
          const display = (user.displayName || '').trim();
          const [fn, ...rest] = display ? display.split(' ') : ['',''];
          
          const updateData = {
            email: user.email
          };
          
          // Only set name fields if they're provided from Google
          if (fn) updateData.firstName = fn;
          if (rest.length > 0) updateData.lastName = rest.join(' ');
          
          await upsertUserProfile(user.uid, updateData);
        } catch (profileErr) {
          console.warn('Failed to upsert profile on Google sign-in:', profileErr?.message || profileErr);
        }
      }
    } catch (error) {
      setError(error.message);
    }
    
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setResetEmailSent(false);
    setLoading(true);

    try {
      await resetPassword(email);
      setResetEmailSent(true);
    } catch (error) {
      setError(error.message);
    }
    
    setLoading(false);
  };

  const handleBackToLogin = () => {
    setIsForgotPassword(false);
    setResetEmailSent(false);
    setError('');
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1 className="login-title">
          {isForgotPassword ? 'Reset Password' : (isSignup ? 'Create Account' : 'Welcome Back')}
        </h1>
        
        {error && <div className="error-message">{error}</div>}
        {resetEmailSent && (
          <div className="success-message" style={{
            padding: '12px',
            backgroundColor: '#d1fae5',
            color: '#065f46',
            borderRadius: '6px',
            marginBottom: '16px',
            fontWeight: 500
          }}>
            Password reset email sent! Please check your inbox.
          </div>
        )}
        
        {isForgotPassword ? (
          // Forgot Password Form
          <form onSubmit={handleForgotPassword} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                placeholder="Enter your email address"
              />
            </div>
            
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
            
            <div className="toggle-mode" style={{ marginTop: 16 }}>
              <p>
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="link-button"
                  disabled={loading}
                >
                  ← Back to Sign In
                </button>
              </p>
            </div>
          </form>
        ) : (
          // Sign In / Sign Up Form
          <>
            <form onSubmit={handleSubmit} className="login-form">
              {isSignup && (
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      type="text"
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name</label>
                    <input
                      type="text"
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                </div>
              )}
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              {isSignup && (
                null
              )}
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    className="link-button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    disabled={loading}
                    style={{ padding: 6, lineHeight: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M15 9l-6 6" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9 9l6 6" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.0" />
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx="12" cy="12" r="3" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              
              {!isSignup && (
                <div style={{ textAlign: 'right', marginBottom: 12 }}>
                  <button
                    type="button"
                    onClick={() => setIsForgotPassword(true)}
                    className="link-button"
                    disabled={loading}
                    style={{ fontSize: '14px' }}
                  >
                    Forgot Password?
                  </button>
                </div>
              )}
              
              <button type="submit" disabled={loading} className="btn btn-primary">
                {loading ? 'Loading...' : (isSignup ? 'Sign Up' : 'Sign In')}
              </button>
            </form>
            
            <div className="divider">
              <span>or</span>
            </div>
            
            <div className="social-buttons">
              <button 
                onClick={handleGoogleSignIn} 
                disabled={loading}
                className="btn btn-google"
              >
                <span className="google-icon">🔍</span>
                Continue with Google
              </button>
            </div>
            
            <div className="toggle-mode">
              <p>
                {isSignup ? 'Already have an account?' : "Don't have an account?"}{' '}
                <button
                  type="button"
                  onClick={() => setIsSignup(!isSignup)}
                  className="link-button"
                  disabled={loading}
                >
                  {isSignup ? 'Sign In' : 'Sign Up'}
                </button>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;